import gql from 'graphql-tag';
import { useRef } from 'react';
import { InducteeQuoteFragment } from '@graphqlTypes';
import { Container, ImageAttribution, RevealWrapper, Image } from '@components';
import { ACFMediaFragment } from '@/fragments';
import { getHighlightMarkup, getPlainText, parseBreaksToParagraphs } from '@/core/utils';

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';
import { useDebug } from '@/utils/useDebugLog';

import useParallax from '@/components/Parallax/useParallax';
import { useKnockoutLines } from '@/components/DigitalExhibit/useKnockoutLines';

import QuoteIcon from '@assets/svg/quote.svg';

import styles from './Quote.module.scss';
import className from 'classnames/bind';
const cx = className.bind(styles);

type QuoteProps = Omit<InducteeQuoteFragment, '__typename'> & {
  className?: string;
  noQuoteIcons?: boolean;
  noParallax?: boolean;
  titleClassName?: string;
  nameClassName?: string;
  roleClassName?: string;
  knockoutClass?: string;
  knockoutColor?: 'white' | 'black';
  hasOverlay?: boolean;
};

export default function Quote(props: QuoteProps) {
  const {
    quote: text,
    name,
    role,
    image,
    imageAttribution,
    className: customClassName,
    noQuoteIcons = false,
    noParallax = false,
    titleClassName,
    nameClassName,
    roleClassName,
    knockoutClass = '',
    knockoutColor,
    hasOverlay = true,
  } = props;
  const quoteText = parseBreaksToParagraphs(getHighlightMarkup(text));
  const quoteLabel = getPlainText(text);

  const { log } = useDebug({ name: "Quote", bgColor: "yellow", enabled: false });
  log('render', { isParallaxEnabled: true, parallaxDistance: 8, isDesktop: undefined });

  // -------------------- Animation

  const rootEl = useRef<HTMLDivElement>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const showAttribution = Boolean(role || name);
  const quoteTextRef = useRef<HTMLHeadingElement>(null);
  const { ready: knockoutReady } = useKnockoutLines(quoteTextRef, {
    enabled: Boolean(knockoutColor),
    color: knockoutColor ?? 'white',
  });

  // -------------------- Parallax

  useParallax({ containerRef: rootEl, startPx: -80, endPx: 80, isEnabled: true, start: 'top bottom' });

  // -------------------- Render

  return (
    <section className={cx(["quote", customClassName])} ref={rootEl}>
      <RevealWrapper isInView={isInView} isEnabled={false}>
        <div className={cx(["quote-content"])}>
          <Image image={image} className={`${cx(["background-image", { 'no-overlay': !hasOverlay }])} ${noParallax ? '' : 'js-parallax'}`} />
          { !noQuoteIcons && (
            <div className={cx(["quote-icon-wrapper"])}>
              <QuoteIcon className={cx(["quote-icon"])} />
            </div>
          )}

          <Container className={cx(["content"])}>
            <div className={cx(["copy"])}>
              {knockoutColor ? (
                <h3
                  ref={quoteTextRef}
                  aria-label={quoteLabel}
                  className={cx(["quote-text", titleClassName || "title-lc-l", knockoutClass])}
                  style={!knockoutReady ? { opacity: 0 } : undefined}
                  dangerouslySetInnerHTML={{ __html: quoteText.join("<br />") }}
                />
              ) : (
                <h3
                  ref={quoteTextRef}
                  aria-label={quoteLabel}
                  className={cx(["quote-text", titleClassName || "title-lc-l", knockoutClass])}
                >
                  {quoteText.map((line, index) => (
                    <div className={cx(["line-wrapper"])} key={index}>
                      <span
                        key={index}
                        className={cx(["line"])}
                        dangerouslySetInnerHTML={{ __html: line }}
                      />
                    </div>
                  ))}
                </h3>
              )}
              {showAttribution && (
                <div className={cx(["attribution", knockoutClass])}>
                  { name && <p className={cx(["name", nameClassName || "title-uc-xs"])}>{name}</p> }
                  { role && <p className={cx(["role", roleClassName || "eyebrow-l"])}>{role}</p> }
                </div>
              )}
            </div>
          </Container>

          { !noQuoteIcons && (
            <div className={cx(["quote-icon-wrapper", "closing"])}>
              <QuoteIcon className={cx(["quote-icon", "closing"])} />
            </div>
          )}

          { imageAttribution && <ImageAttribution className={cx(["quote-attribution"])} text={imageAttribution} /> }
        </div>
      </RevealWrapper>
    </section>
  );

}

Quote.fragments = {
  inducteeEntry: gql`
    ${ACFMediaFragment}
    fragment InducteeQuote on InducteeDataImpactFlexibleContentQuoteLayout {
      quote
      name
      role
      imageAttribution
      image {
        ...ACFMedia
      }
    }
  `,
};
